// src/pages/evenements/baptemes/ListeBaptemes.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Modal, Alert, Button, Table, Pagination, Form, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../../layouts/Header';
import Footer from '../../../layouts/Footer';
import { ROLES } from '../../../data/Menu';
import { COLORS } from '../../../constants/colors';

// Fonctions utilitaires
const truncateText = (text, maxLength = 25) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatDateSimple = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const getTypeColor = (type) => {
  const typeLower = type?.toLowerCase() || '';
  if (typeLower.includes('eau')) return COLORS.cyan;
  if (typeLower.includes('esprit') || typeLower.includes('saint')) return COLORS.warning;
  return COLORS.primary;
};

const getTypeIcon = (type) => {
  const typeLower = type?.toLowerCase() || '';
  if (typeLower.includes('eau')) return 'ri-water-flash-line';
  if (typeLower.includes('esprit') || typeLower.includes('saint')) return 'ri-fire-line';
  return 'ri-question-line';
};

const getTypeLabel = (type) => {
  const typeLower = type?.toLowerCase() || '';
  if (typeLower.includes('eau')) return 'Baptême d\'eau';
  if (typeLower.includes('esprit') || typeLower.includes('saint')) return 'Saint-Esprit';
  return type || 'Inconnu';
};

export default function ListeBaptemes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [egliseFilter, setEgliseFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [eglises, setEglises] = useState([]);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [baptemeToDelete, setBaptemeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    eau: 0,
    esprit: 0
  });

  const [permissions, setPermissions] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canFilter: false
  });

  // ===== Permissions =====
  const hasPermission = (action) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      const user = JSON.parse(userStr);
      const userRole = user.role || user.type_utilisateur;
      if (userRole === ROLES.SUPER_ADMIN) return true;

      const permissionsMap = {
        'view_baptemes': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE],
        'add_bapteme': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'edit_bapteme': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'delete_bapteme': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
        'export_baptemes': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL],
        'filter_baptemes': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE]
      };
      return permissionsMap[action]?.includes(userRole) || false;
    } catch (e) {
      console.error('Erreur permission:', e);
      return false;
    }
  };

  useEffect(() => {
    setPermissions({
      canView: hasPermission('view_baptemes'),
      canAdd: hasPermission('add_bapteme'),
      canEdit: hasPermission('edit_bapteme'),
      canDelete: hasPermission('delete_bapteme'),
      canExport: hasPermission('export_baptemes'),
      canFilter: hasPermission('filter_baptemes')
    });
  }, []);

  // ===== API =====
  const fetchEglises = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setEglises(result.eglises || []);
      }
    } catch (err) {
      console.error('Erreur chargement églises:', err);
    }
  }, []);

  const fetchBaptemes = useCallback(async () => {
    if (data) setFilterLoading(true);
    else setLoading(true);

    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;

      const timestamp = Date.now();
      let url = `https://josephsbusiness.com/api/baptemes.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (egliseFilter) url += `&eglise=${encodeURIComponent(egliseFilter)}`;
      if (typeFilter) url += `&type=${encodeURIComponent(typeFilter)}`;

      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setData(result);
        if (result.baptemes) {
          let eau = 0, esprit = 0;
          result.baptemes.forEach(bapteme => {
            const typeLower = bapteme.type?.toLowerCase() || '';
            if (typeLower.includes('eau')) eau++;
            else if (typeLower.includes('esprit') || typeLower.includes('saint')) esprit++;
          });
          setStats({
            total: result.pagination?.total || 0,
            eau,
            esprit
          });
        }
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  }, [currentPage, searchTerm, egliseFilter, typeFilter, itemsPerPage, navigate]); // <- data retiré

  useEffect(() => {
    fetchEglises();
  }, [fetchEglises]);

  useEffect(() => {
    fetchBaptemes();
  }, [fetchBaptemes, refreshTrigger]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reload = params.get('reload');
    if (reload) {
      setRefreshTrigger(prev => prev + 1);
      navigate('/evenements/baptemes', { replace: true });
    }
  }, [location, navigate]);

  // ===== Handlers =====
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (bapteme) => {
    setBaptemeToDelete(bapteme);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!baptemeToDelete) return;
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const response = await fetch(`https://josephsbusiness.com/api/baptemes.php?user_id=${userId}&id=${baptemeToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setShowDeleteModal(false);
        setBaptemeToDelete(null);
        setRefreshTrigger(prev => prev + 1);
        setSuccessMessage('Baptême supprimé avec succès');
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        alert(result.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const getEgliseName = (matricule) => {
    const eglise = eglises.find(e => e.matricule === matricule);
    return eglise ? eglise.nom : matricule;
  };

  const exportToExcel = () => {
    if (!data?.baptemes) return;
    const fileName = `baptemes_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = data.baptemes.map(item => ({
      'Matricule': item.matricule,
      'Nom': item.nom,
      'Type': item.type,
      'Date': formatDateSimple(item.date),
      'Lieu': item.lieu || '-',
      'Pasteur': item.matri_past ? `${item.pasteur_nom} ${item.pasteur_prenom}` : '-',
      'Église': getEgliseName(item.matri_eglise)
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.sheet_add_aoa(ws, [
      ['LISTE DES BAPTÊMES'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [`Total: ${stats.total} baptêmes`],
      []
    ], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, "Baptêmes");
    XLSX.writeFile(wb, fileName);
  };

  const printPDF = () => {
    const printWindow = window.open('', '_blank');
    const baptemes = data?.baptemes || [];
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste des baptêmes</title>
        <meta charset="UTF-8">
        <style>
          body { font-family: Arial; margin: 30px; }
          .header { text-align: center; border-bottom: 2px solid #295AA7; margin-bottom: 30px; }
          h1 { color: #295AA7; }
          .summary { background: #f8fafc; padding: 20px; border-left: 4px solid #06b6d4; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #295AA7; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border: 1px solid #ddd; }
          tr:nth-child(even) { background: #f8fafc; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Liste des baptêmes</h1>
          <p>Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
        </div>
        <div class="summary">
          <p><strong>Total baptêmes:</strong> ${stats.total}</p>
          <p><strong>Baptêmes d'eau:</strong> ${stats.eau} | <strong>Saint-Esprit:</strong> ${stats.esprit}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>Matricule</th>
              <th>Nom</th>
              <th>Type</th>
              <th>Date</th>
              <th>Lieu</th>
              <th>Pasteur</th>
              <th>Église</th>
            </tr>
          </thead>
          <tbody>
            ${baptemes.map(b => `
              <tr>
                <td>${b.matricule}</td>
                <td>${truncateText(b.nom, 20)}</td>
                <td>${truncateText(b.type, 20)}</td>
                <td>${formatDateSimple(b.date)}</td>
                <td>${b.lieu ? truncateText(b.lieu, 15) : '-'}</td>
                <td>${b.matri_past ? truncateText(b.pasteur_nom + ' ' + b.pasteur_prenom, 15) : '-'}</td>
                <td>${truncateText(getEgliseName(b.matri_eglise), 15)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  // ===== Loading =====
  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des baptêmes</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => setRefreshTrigger(prev => prev + 1)}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const baptemes = data?.baptemes || [];
  const pagination = data?.pagination || { total: 0, pages: 1 };

  // Pagination
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    const totalPages = pagination.pages;
    if (totalPages <= 1) return items;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)} className="pagination-item">
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled className="pagination-ellipsis" />);
      }
    }
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage} 
          onClick={() => handlePageChange(number)}
          className="pagination-item"
        >
          {number}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled className="pagination-ellipsis" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)} className="pagination-item">
          {totalPages}
        </Pagination.Item>
      );
    }
    return items;
  };

  // ===== Rendu principal =====
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: `linear-gradient(135deg, ${COLORS.info}, #0891b2)` }}>
                <i className="ri-water-flash-line" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div>
                <h5 className="gradient-text">Baptêmes</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-water-flash-line"></i> Total {stats.total}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.cyan}15`, color: COLORS.cyan }}>
                    <i className="ri-water-flash-line"></i> Eau {stats.eau}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                    <i className="ri-fire-line"></i> Esprit {stats.esprit}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {permissions.canFilter && (
                <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                  <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                </Button>
              )}
              {permissions.canExport && (
                <>
                  <Button size="sm" className="btn-modern-outline" onClick={exportToExcel}>
                    <i className="ri-file-excel-line"></i> Excel
                  </Button>
                  <Button size="sm" className="btn-modern-outline" onClick={printPDF}>
                    <i className="ri-file-pdf-line"></i> PDF
                  </Button>
                </>
              )}
              {permissions.canAdd && (
                <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/evenements/baptemesNouveau')}>
                  <i className="ri-add-line"></i> Nouveau
                </Button>
              )}
              <Button size="sm" className="btn-modern-outline" onClick={() => setRefreshTrigger(prev => prev + 1)} disabled={filterLoading}>
                <i className={`ri-refresh-line ${filterLoading ? 'fa-spin' : ''}`}></i> Actualiser
              </Button>
            </div>
          </div>

          {/* ===== MESSAGE DE SUCCÈS ===== */}
          {showSuccessMessage && (
            <Alert
              variant="success"
              className="border-0 shadow-sm rounded-3 mb-3"
              onClose={() => setShowSuccessMessage(false)}
              dismissible
            >
              <div className="d-flex align-items-center">
                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                <span>{successMessage}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col sm="6" xl="4">
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-water-flash-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total</span>
                  <h3 className="kpi-value">{stats.total}</h3>
                </div>
              </div>
            </Col>
            <Col sm="6" xl="4">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.cyan }}>
                <div className="kpi-icon" style={{ background: 'rgba(6,182,212,0.1)', color: COLORS.cyan }}>
                  <i className="ri-water-flash-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Baptêmes d'eau</span>
                  <h3 className="kpi-value">{stats.eau}</h3>
                </div>
              </div>
            </Col>
            <Col sm="6" xl="4">
              <div className="kpi-card kpi-warning">
                <div className="kpi-icon"><i className="ri-fire-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Saint-Esprit</span>
                  <h3 className="kpi-value">{stats.esprit}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== FILTRES (fond primaryDark) ===== */}
          {permissions.canFilter && showFilters && (
            <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
              <Card.Body className="py-3 px-4">
                <Form onSubmit={handleSearch}>
                  <Row className="align-items-center g-3">
                    <Col md="4">
                      <div className="position-relative">
                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50 fs-14"></i>
                        <Form.Control
                          type="text"
                          placeholder="Nom, matricule..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="ps-5 rounded-pill"
                          style={{ background: 'rgba(255,255,255,0.9)' }}
                        />
                      </div>
                    </Col>
                    <Col md="3">
                      <Form.Select
                        value={egliseFilter}
                        onChange={(e) => setEgliseFilter(e.target.value)}
                        className="rounded-pill px-3"
                        style={{ background: 'rgba(255,255,255,0.9)' }}
                      >
                        <option value="">Église</option>
                        {eglises.map(eglise => (
                          <option key={eglise.matricule} value={eglise.matricule}>
                            {truncateText(eglise.nom, 15)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md="3">
                      <Form.Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="rounded-pill px-3"
                        style={{ background: 'rgba(255,255,255,0.9)' }}
                      >
                        <option value="">Type</option>
                        <option value="eau">Baptême d'eau</option>
                        <option value="esprit">Saint-Esprit</option>
                      </Form.Select>
                    </Col>
                    <Col md="2">
                      <Button
                        type="submit"
                        variant="light"
                        size="sm"
                        className="rounded-pill w-100"
                        disabled={filterLoading}
                      >
                        {filterLoading ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* ===== TABLEAU (en‑tête secondary) ===== */}
          <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
            <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <i className="ri-list-check text-white me-2 fs-5"></i>
                  <span className="fw-semibold text-white">Liste des baptêmes</span>
                  <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                    {pagination.total} au total
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto', background: 'rgba(255,255,255,0.9)' }}
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </Form.Select>
                  {filterLoading && <Spinner animation="border" size="sm" variant="light" />}
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="ps-4">Matricule</th>
                      <th>Nom</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Lieu</th>
                      <th>Pasteur</th>
                      <th>Église</th>
                      <th className="text-center pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baptemes.length > 0 ? (
                      baptemes.map((bapteme) => {
                        const typeColor = getTypeColor(bapteme.type);
                        const typeIcon = getTypeIcon(bapteme.type);
                        const typeLabel = getTypeLabel(bapteme.type);
                        return (
                          <tr key={bapteme.id}>
                            <td className="ps-4">
                              <span className="reference-badge">{bapteme.matricule}</span>
                            </td>
                            <td>{truncateText(bapteme.nom, 15)}</td>
                            <td>
                              <span
                                className="badge rounded-pill"
                                style={{
                                  backgroundColor: typeColor,
                                  color: 'white',
                                  padding: '0.25rem 0.6rem',
                                  fontSize: '0.7rem'
                                }}
                              >
                                <i className={typeIcon + ' me-1'}></i>
                                {truncateText(typeLabel, 12)}
                              </span>
                            </td>
                            <td>{formatDateSimple(bapteme.date)}</td>
                            <td>{bapteme.lieu ? truncateText(bapteme.lieu, 12) : '-'}</td>
                            <td>
                              {bapteme.matri_past ? (
                                truncateText(`${bapteme.pasteur_nom || ''} ${bapteme.pasteur_prenom || ''}`, 12)
                              ) : '-'}
                            </td>
                            <td>{truncateText(bapteme.eglise_nom, 12)}</td>
                            <td className="text-center pe-4">
                              <div className="d-flex gap-2 justify-content-center">
                                {permissions.canEdit && (
                                  <button
                                    className="action-icon action-icon-primary"
                                    onClick={() => navigate(`/evenements/baptemes/modifier/${bapteme.id}`)}
                                    title="Modifier"
                                  >
                                    <i className="ri-pencil-line"></i>
                                  </button>
                                )}
                                {permissions.canDelete && (
                                  <button
                                    className="action-icon action-icon-danger"
                                    onClick={() => handleDeleteClick(bapteme)}
                                    title="Supprimer"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                          <h6 className="mb-1">Aucun baptême trouvé</h6>
                          <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          {/* ===== PAGINATION ===== */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4 px-3">
              <div className="text-muted small">
                Page {currentPage} sur {pagination.pages}
              </div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {renderPaginationItems()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
              </Pagination>
            </div>
          )}

          <Footer />
        </div>
      </div>

      {/* ===== MODAL DE SUPPRESSION ===== */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="md"
        className="delete-modal-elegant"
      >
        <Modal.Header closeButton className="border-0 pt-4">
          <Modal.Title className="w-100 text-center">
            <div className="delete-icon-wrapper mb-3">
              <div className="delete-icon-circle" style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${COLORS.danger}15, ${COLORS.rose}15)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                animation: 'pulseIcon 2s infinite'
              }}>
                <div className="delete-icon-inner" style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${COLORS.danger}, ${COLORS.rose})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 20px rgba(239, 68, 68, 0.3)'
                }}>
                  <i className="ri-delete-bin-5-line" style={{ fontSize: '32px', color: 'white' }}></i>
                </div>
              </div>
            </div>
            <h5 className="fw-bold mb-1" style={{ color: COLORS.primary, fontSize: '1.25rem' }}>
              Supprimer ce baptême ?
            </h5>
            <p className="text-muted small mb-0">Cette action est irréversible</p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          {baptemeToDelete && (
            <div className="delete-content">
              <div className="bapteme-card mb-3" style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px',
                padding: '1.25rem',
                border: '1px solid rgba(0,0,0,0.05)'
              }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="type-icon" style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: `${getTypeColor(baptemeToDelete.type)}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className={getTypeIcon(baptemeToDelete.type)} style={{
                      fontSize: '24px',
                      color: getTypeColor(baptemeToDelete.type)
                    }}></i>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="fw-bold mb-1" style={{ color: COLORS.primary, fontSize: '1.1rem' }}>
                      {truncateText(baptemeToDelete.nom, 30)}
                    </h6>
                    <span className="badge" style={{
                      background: getTypeColor(baptemeToDelete.type),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.7rem'
                    }}>
                      <i className={getTypeIcon(baptemeToDelete.type) + ' me-1'}></i>
                      {getTypeLabel(baptemeToDelete.type)}
                    </span>
                  </div>
                </div>

                <div className="info-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.75rem',
                  marginTop: '0.5rem'
                }}>
                  <div className="info-item" style={{
                    background: 'white',
                    padding: '0.5rem',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <i className="ri-calendar-line" style={{ color: COLORS.primary, fontSize: '1rem' }}></i>
                    <span className="ms-1 small fw-medium">{formatDateSimple(baptemeToDelete.date)}</span>
                  </div>
                  <div className="info-item" style={{
                    background: 'white',
                    padding: '0.5rem',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <i className="ri-map-pin-line" style={{ color: COLORS.primary, fontSize: '1rem' }}></i>
                    <span className="ms-1 small fw-medium">{baptemeToDelete.lieu || 'Non précisé'}</span>
                  </div>
                </div>

                {baptemeToDelete.matri_past && (
                  <div className="pasteur-info mt-3 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                    <div className="text-center">
                      <i className="ri-user-star-line me-1" style={{ color: COLORS.primary, fontSize: '0.8rem' }}></i>
                      <span className="small text-muted">
                        {baptemeToDelete.pasteur_nom || ''} {baptemeToDelete.pasteur_prenom || ''}
                      </span>
                    </div>
                  </div>
                )}

                {baptemeToDelete.eglise_nom && (
                  <div className="eglise-info mt-2 pt-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.1)', textAlign: 'center' }}>
                    <i className="ri-church-line me-1" style={{ color: COLORS.primary, fontSize: '0.8rem' }}></i>
                    <span className="small text-muted">{baptemeToDelete.eglise_nom}</span>
                  </div>
                )}
              </div>

              <div className="warning-message" style={{
                background: `${COLORS.danger}10`,
                border: `1px solid ${COLORS.danger}30`,
                borderRadius: '12px',
                padding: '0.8rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.8rem'
              }}>
                <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
                <span className="small text-muted">
                  Toutes les données associées à ce baptême seront définitivement supprimées.
                </span>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center gap-3">
          <Button
            variant="light"
            size="md"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
            className="px-4"
            style={{
              borderRadius: '30px',
              padding: '0.5rem 2rem',
              background: '#f1f5f9',
              border: 'none',
              color: '#64748b',
              fontWeight: '500'
            }}
          >
            <i className="ri-close-line me-1"></i> Annuler
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            className="px-4"
            style={{
              borderRadius: '30px',
              padding: '0.5rem 2rem',
              background: `linear-gradient(135deg, ${COLORS.danger}, ${COLORS.rose})`,
              border: 'none',
              color: 'white',
              fontWeight: '500',
              boxShadow: `0 8px 16px ${COLORS.danger}30`
            }}
          >
            {deleting ? (
              <><Spinner size="sm" className="me-2" />Suppression...</>
            ) : (
              <><i className="ri-delete-bin-5-line me-1"></i>Supprimer</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }
        .btn-modern-primary {
          background: ${COLORS.primary};
          border: none;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: white;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }
        .btn-modern-primary:hover {
          background: ${COLORS.primaryDark};
          box-shadow: 0 4px 10px rgba(41,90,167,0.3);
        }

        /* KPI */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-success { border-left-color: ${COLORS.success}; }
        .kpi-danger { border-left-color: ${COLORS.danger}; }
        .kpi-warning { border-left-color: ${COLORS.warning}; }
        .kpi-info { border-left-color: ${COLORS.info}; }

        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-success .kpi-icon { background: rgba(16, 185, 129, 0.1); color: ${COLORS.success}; }
        .kpi-danger .kpi-icon { background: rgba(239, 68, 68, 0.1); color: ${COLORS.danger}; }
        .kpi-warning .kpi-icon { background: rgba(245, 158, 11, 0.1); color: ${COLORS.warning}; }
        .kpi-info .kpi-icon { background: rgba(59, 130, 246, 0.1); color: ${COLORS.info}; }

        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }

        /* Actions */
        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          color: #6c757d;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-icon:hover {
          background: ${COLORS.primary};
          border-color: ${COLORS.primary};
          color: white !important;
        }
        .action-icon-primary:hover {
          background: ${COLORS.primary};
          border-color: ${COLORS.primary};
          color: white !important;
        }
        .action-icon-danger:hover {
          background: #dc3545;
          border-color: #dc3545;
          color: white !important;
        }

        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-family: monospace;
        }

        .fs-24 { font-size: 1.5rem; }
        .fs-48 { font-size: 3rem; }
        .fs-14 { font-size: 0.875rem; }
        .rounded-3 { border-radius: 0px !important; }
        .rounded-pill { border-radius: 50px !important; }

        /* Modal */
        .delete-modal-elegant .modal-content {
          border: none;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
          animation: slideUp 0.3s ease-out;
        }
        .delete-modal-elegant .modal-header {
          background: linear-gradient(135deg, #ffffff, #f8fafc);
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseIcon {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.9; }
        }
        .delete-icon-wrapper { animation: pulseIcon 2s infinite; }
        .bapteme-card { transition: all 0.2s ease; }
        .bapteme-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.05); }
        .info-item { transition: all 0.2s ease; }
        .info-item:hover { background: #ffffff !important; box-shadow: 0 4px 8px rgba(0,0,0,0.02); }
        .warning-message { transition: all 0.2s ease; }
        .warning-message:hover { background: ${COLORS.danger}10 !important; }
        .modal-footer button { transition: all 0.3s ease; }
        .modal-footer button:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
        .modal-footer button:active { transform: translateY(0); }

        /* Table */
        .table thead th {
          background: #f8fafc;
          color: #475569;
          font-weight: 600;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        .table td {
          white-space: nowrap;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .table tbody tr:hover {
          background: #f8fafc !important;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
          .table td {
            max-width: 80px;
          }
        }
      `}</style>
    </>
  );
}